import {
	Category,
	FacetModel,
	FacetType,
	MediaModel,
	SearchProductWithSkusModel,
	SearchSkuInformationModel,
	UnifiedSearchResultModel,
} from '@bizzkit/ecommerce-search/client';
import { InstalledEquipment } from '@/api/appSolutionBuilder/types';
import { DefaultPageSize, ProductListPageQuery } from '@/api/product/search';
import {
	AddOnProduct,
	Filter,
	FilterType,
	MediaModelType,
	PriceSliderValues,
	ProductDetail,
	ProductFile,
	ProductItemResponse,
	ProductSearchResult,
	ProductSibling,
	ProductSimpleVariant,
	ProductSort,
	ProductVariant,
	ProductVideo,
	QuickSearchResult,
	SortDirection,
	SortingOption,
} from '@/api/product/types';
import DefaultImage from '@/assets/images/defaultImage.jpg';
import {
	MediaBankFileInfoType,
	MediaMetaData,
	MediaTypeMetaData,
	ProductAttributesMetaData,
	ImageSizeResponse,
	SearchSettingsResponse,
	SiblingMetaData,
	SkuAttributesMetaData,
	SkuMediaItemMetaData,
	SpecsMetaData,
	StockStatus,
	TranslationsWebsite,
	VideoMetaData,
	ImageResponse,
	IncludedItemMetaData,
} from '@/generated/data.schemas';

export const fallbackImage = {
	altText: '',
	size180: { url: DefaultImage },
	size320: { url: DefaultImage },
	size480: { url: DefaultImage },
	size720: { url: DefaultImage },
	size960: { url: DefaultImage },
	size1280: { url: DefaultImage },
	size1920: { url: DefaultImage },
};

const mapPrice = (price: number | undefined | null): number => {
	if (!price) return 0;
	return price / 100; // ECS uses "cents" as the price
};

const mapStockStatus = (stockStatus: StockStatus): number => {
	switch (stockStatus) {
		case StockStatus.InStock:
			return 11;
		case StockStatus.FewInStock:
			return 1;
		case StockStatus.OutOfStock:
			return 0;
		default:
			return 0;
	}
};

const mapProductAttributes = (metadata: Record<string, string> | undefined): ProductAttributesMetaData => {
	const attributeMetaData = metadata && metadata.ProductAttributes;
	return JSON.parse(attributeMetaData || '{}') as ProductAttributesMetaData;
};

const mapSkuAttributes = (metadata: Record<string, string> | undefined): SkuAttributesMetaData => {
	const attributeMetaData = metadata && metadata.SkuAttributes;
	return JSON.parse(attributeMetaData || '{}') as SkuAttributesMetaData;
};

const mapProductSpecs = (metadata: Record<string, string> | undefined): Record<string, string> => {
	const specsData = metadata && metadata.ProductSpecsData;
	const specs = JSON.parse(specsData || '{}') as SpecsMetaData;
	return specs.specs || {};
};

const mapSkuSpecs = (metadata: Record<string, string> | undefined): Record<string, string> => {
	const specsData = metadata && metadata.SkuSpecsData;
	const specs = JSON.parse(specsData || '{}') as SpecsMetaData;
	return specs.specs || {};
};

export const mapImageSize = (model: MediaTypeMetaData | undefined): ImageSizeResponse => {
	return {
		url: model?.url || '',
		width: model?.width,
		height: model?.height,
	};
};

const mapMediaModel = (model: MediaModel | undefined, altText: string | undefined | null): ImageResponse => {
	const imageMetaDataJson = model && model.metadata;
	if (!imageMetaDataJson) return fallbackImage;

	const imageMetaData = JSON.parse(imageMetaDataJson || '{}') as MediaMetaData;
	if (!imageMetaData.types) return fallbackImage;

	return {
		altText: altText,
		size180: mapImageSize(imageMetaData.types.find((t) => t.type === MediaBankFileInfoType.Size180)),
		size320: mapImageSize(imageMetaData.types.find((t) => t.type === MediaBankFileInfoType.Size320)),
		size480: mapImageSize(imageMetaData.types.find((t) => t.type === MediaBankFileInfoType.Size480)),
		size720: mapImageSize(imageMetaData.types.find((t) => t.type === MediaBankFileInfoType.Size720)),
		size960: mapImageSize(imageMetaData.types.find((t) => t.type === MediaBankFileInfoType.Size960)),
		size1280: mapImageSize(imageMetaData.types.find((t) => t.type === MediaBankFileInfoType.Size1280)),
		size1920: mapImageSize(imageMetaData.types.find((t) => t.type === MediaBankFileInfoType.Size1920)),
	};
};

const mapVideoModel = (model: MediaModel | undefined): ProductVideo => {
	const videoMetaDataJson = model && model.metadata;
	if (!videoMetaDataJson) return { title: '', url: '', thumbnailUrl: '', uniqueVideoId: '' };

	const videoMetaData = JSON.parse(videoMetaDataJson || '{}') as VideoMetaData;
	if (!videoMetaData.uniqueVideoId) return { title: '', url: '', thumbnailUrl: '', uniqueVideoId: '' };

	return {
		title: videoMetaData.title,
		url: model.url || '',
		thumbnailUrl: `//img.youtube.com/vi/${videoMetaData.uniqueVideoId}/maxresdefault.jpg`,
		uniqueVideoId: videoMetaData.uniqueVideoId,
	};
};

export const mapSkuMediaToImages = (
	media: SkuMediaItemMetaData[] | undefined,
	altText: string,
): ImageResponse[] | null => {
	if (!media || media.length === 0) return null;
	return media.map((m) => mapMediaModel(m as unknown as MediaModel, altText));
};

export const mapSkuMediaToVideos = (media: SkuMediaItemMetaData[] | undefined): ProductVideo[] | null => {
	if (!media || media.length === 0) return null;
	return media.map((m) => mapVideoModel(m as unknown as MediaModel));
};

const mapFileModel = (model: MediaModel | undefined): ProductFile => {
	const fileMetaDataJson = model && model.metadata;
	if (!fileMetaDataJson) return { name: '', url: '', fileType: '' };

	const fileMetaData = JSON.parse(fileMetaDataJson || '{}') as MediaMetaData;
	if (!fileMetaData.fileName) return { name: '', url: '', fileType: '' };

	const extension = fileMetaData.fileName.split('.').pop();
	return {
		name: fileMetaData.fileName || '',
		url: model.url || '',
		fileType: extension || '',
	};
};

const mapSiblings = (metadata: Record<string, string> | undefined): ProductSibling[] => {
	const siblingsData = metadata && metadata.Siblings;
	if (!siblingsData) return [];

	const siblings = JSON.parse(siblingsData || '[]') as SiblingMetaData[];
	if (!siblings) return [];

	function mapSibling(s: SiblingMetaData): ProductSibling {
		const imageMetaData = JSON.parse(s.imageMetaData || '{}') as MediaMetaData;

		return {
			id: s.id,
			name: s.name,
			url: s.url,
			image: {
				altText: s.name,
				size180: mapImageSize(imageMetaData.types?.find((t) => t.type === MediaBankFileInfoType.Size180)),
				size320: mapImageSize(imageMetaData.types?.find((t) => t.type === MediaBankFileInfoType.Size320)),
				size480: mapImageSize(imageMetaData.types?.find((t) => t.type === MediaBankFileInfoType.Size480)),
				size720: mapImageSize(imageMetaData.types?.find((t) => t.type === MediaBankFileInfoType.Size720)),
				size960: mapImageSize(imageMetaData.types?.find((t) => t.type === MediaBankFileInfoType.Size960)),
				size1280: mapImageSize(imageMetaData.types?.find((t) => t.type === MediaBankFileInfoType.Size1280)),
				size1920: mapImageSize(imageMetaData.types?.find((t) => t.type === MediaBankFileInfoType.Size1920)),
			},
		};
	}

	return siblings.map((s) => mapSibling(s));
};

const getPrimaryCategory = (categories: Category[]): Category | null => {
	if (categories.length > 0) {
		return categories[categories.length - 1];
	}

	return null;
};

const getCategoryHierarchy = (
	categories: Category[][] | null | undefined,
	categoryRootSystemName: string | undefined,
): Category[] => {
	if (!categories || categories.length === 0) {
		return [];
	}
	const hierarchy = categories.find((x) => x.find((y) => y.id == categoryRootSystemName));
	if (!hierarchy) {
		return [];
	}

	// the first element is the root hierarchy itself
	return hierarchy.slice(1);
};

export const mapInstalledEquipment = (p: SearchProductWithSkusModel): InstalledEquipment[] => {
	const skus = p.skus;

	return (
		skus?.map((sku) => {
			const insertType = Object.values(sku.stringAttribute?.inserttype?.values || {})?.join(',') || '';
			const pumpType = Object.values(sku.stringAttribute?.pumptype?.values || {})?.join(',') || '';
			const skuAttributes = mapSkuAttributes(sku.metadata);
			return {
				id: sku.id,
				name: sku.name || sku.id || '',
				insertQuantity: skuAttributes.inserts?.[0]?.quantity,
				insertProductId: skuAttributes.inserts?.[0]?.productId,
				skuNo: sku.skuNo,
				insertType,
				pumpType,
			};
		}) || []
	);
};

export const mapProductItem = (
	p: SearchProductWithSkusModel & {
		metaTitle?: string | null;
		metaDescription?: string | null;
		metaKeywords?: string | null;
	},
	settings: SearchSettingsResponse | undefined,
): ProductItemResponse => {
	const productAttributes = mapProductAttributes(p.metadata);
	const mainImage = p.media?.find((m) => m.type === 'Image');
	const image = mapMediaModel(mainImage, p.name);

	const categories = getCategoryHierarchy(p.categories, settings?.productCategoryRootSystemName);
	const primaryCategory = getPrimaryCategory(categories);

	return {
		id: p.id || '',
		itemNo: p.productNumber,
		name: p.name || p.id || '',
		brand: primaryCategory?.name != null ? primaryCategory.name : '',
		url: productAttributes.url,
		image: image,
		variants: mapSimpleVariants(p.skus) || [],
		skus: p.skus,
	};
};

const mapFacet = (f: FacetModel): Filter => {
	const filterType = f.facetType === FacetType.Range ? FilterType.Number : FilterType.String;
	const id = f.key ?? '';

	return {
		id,
		displayName: f.name ?? id,
		type: filterType,
		max: f.max,
		min: f.min,
		selectedMax: f.selectedTo,
		selectedMin: f.selectedFrom,
		values:
			f.values?.map((v) => {
				return {
					label: v.key || '',
					value: v.key || '',
					hitCount: v.count || 0,
					isSelected: v.isSelected || false,
				};
			}) || [],
	};
};

export const mapFacets = (
	facets: FacetModel[] | null | undefined,
	settings: SearchSettingsResponse | undefined,
): Filter[] => {
	if (!facets) {
		return [];
	}
	return facets
		.filter(
			(f) =>
				f.key !== settings?.priceFilterId &&
				f.key !== settings?.categoryIdsFilterId &&
				f.key !== settings?.categoryNamesFilterId,
		)
		.filter((f) => !!f.values?.length || (f.min && f.max))
		.map((f) => mapFacet(f));
};

const mapPriceSliderValues = (
	facets: FacetModel[] | null | undefined,
	settings: SearchSettingsResponse | undefined,
): PriceSliderValues => {
	const facet = facets?.find((f) => f.key === settings?.priceFilterId);
	if (!facet) {
		return { minPrice: 0, maxPrice: 0, selectedMinPrice: null, selectedMaxPrice: null };
	}
	const mapped = mapFacet(facet);

	return {
		minPrice: mapPrice(mapped.min),
		maxPrice: mapPrice(mapped.max),
		selectedMinPrice:
			mapped.selectedMin !== null && mapped.selectedMin !== undefined ? mapPrice(mapped.selectedMin) : null,
		selectedMaxPrice:
			mapped.selectedMax !== null && mapped.selectedMax !== undefined ? mapPrice(mapped.selectedMax) : null,
	};
};

const mapSimpleVariant = (s: SearchSkuInformationModel): ProductSimpleVariant => {
	return {
		id: s.id || '',
		name: s.name || '',
	};
};

const mapSimpleVariants = (skus?: SearchSkuInformationModel[] | null): ProductSimpleVariant[] => {
	if (!skus) {
		return [];
	}

	return skus.map((s) => mapSimpleVariant(s));
};

const mapVariant = (s: SearchSkuInformationModel): ProductVariant => {
	const variantAttributes = mapSkuAttributes(s.metadata);
	return {
		id: s.id || '',
		itemNo: s.skuNo,
		name: s.name || '',
		stock: mapStockStatus(variantAttributes.stockStatus),
		size: variantAttributes.size,
		specs: mapSkuSpecs(s.metadata),
		selectors: variantAttributes.variantAttributes || [],
		disableAddToBasket: variantAttributes.disableAddToBasket ?? false,
		inserts: variantAttributes.inserts,
		mandatoryAddOns: variantAttributes.mandatoryAddOns,
		spareParts: variantAttributes.spareParts,
		brand: variantAttributes.brand,
		media: variantAttributes.media,
		splashText: variantAttributes.splashText,
		accessories: variantAttributes.accessories?.map((x) => x.productId) ?? [],
		associatedProducts: variantAttributes.associatedProducts,
		shortDescription: variantAttributes.shortDescription,
		longDescription: variantAttributes.longDescription,
		metaDescription: variantAttributes.metaDescription,
		metaKeywords: variantAttributes.metaKeywords,
		metaTitle: variantAttributes.metaTitle,
	};
};

const mapVariants = (skus?: SearchSkuInformationModel[] | null): ProductVariant[] => {
	if (!skus) {
		return [];
	}

	return skus.map((s) => mapVariant(s));
};

const getSortingOptions = (translations: TranslationsWebsite): SortingOption[] => {
	return [
		{
			sortBy: ProductSort.Name,
			sortDirection: SortDirection.Ascending,
			displayName: translations.productList.sorting.nameAscending,
		},
		{
			sortBy: ProductSort.Name,
			sortDirection: SortDirection.Descending,
			displayName: translations.productList.sorting.nameDescending,
		},
		{
			sortBy: ProductSort.Price,
			sortDirection: SortDirection.Ascending,
			displayName: translations.productList.sorting.priceAscending,
		},
		{
			sortBy: ProductSort.Price,
			sortDirection: SortDirection.Descending,
			displayName: translations.productList.sorting.priceDescending,
		},
		{
			sortBy: ProductSort.Score,
			sortDirection: SortDirection.Descending,
			displayName: translations.productList.sorting.relevance,
		},
	];
};

export const mapQuickSearchResult = (
	data: UnifiedSearchResultModel,
	phrase: string,
	settings: SearchSettingsResponse | undefined,
): QuickSearchResult => {
	return {
		phrase: phrase,
		searchSuggestions: data.suggestions || [],
		products: data.products?.map((p) => mapProductItem(p, settings)) || [],
		totalHits: data.totalProducts || 0,
		totalContentHits: data.totalContent || 0,
		content: data.content || [],
	};
};

export const mapSearchResult = (
	data: UnifiedSearchResultModel | undefined,
	query: ProductListPageQuery,
	translations: TranslationsWebsite | undefined,
	categoryIds: string[] | undefined,
	settings: SearchSettingsResponse | undefined,
): ProductSearchResult | undefined => {
	if (!data || !translations) {
		return undefined;
	}

	const sortingOptions = getSortingOptions(translations);

	const currentSorting = query.sortBy
		? sortingOptions.find((s) => s.sortBy === query.sortBy && s.sortDirection === query.sortDirection)
		: sortingOptions.find((s) => s.sortBy === ProductSort.Score);

	return {
		products: data.products?.map((p) => mapProductItem(p, settings)) || [],
		phrase: data.usedPhrase,
		didYouMean: data.didYouMean?.find((m) => m !== undefined) || '',
		filters: mapFacets(data.facets, settings) || [],
		sorting: sortingOptions,
		hasCategory: query.productCategoryId !== undefined,
		pagingInformation: {
			currentPage: (data.productOffset || 0) / (query.pageSize || DefaultPageSize) + 1,
			pageSize: query.pageSize || DefaultPageSize,
			totalNumberOfItems: data.totalProducts || 0,
		},
		productCategoryIds: categoryIds || [],
		currentSorting: currentSorting,
		priceSliderValues: mapPriceSliderValues(data.facets, settings),
	};
};

export const mapProductDetail = (
	p: SearchProductWithSkusModel,
	origin: string,
	settings: SearchSettingsResponse | undefined,
): ProductDetail => {
	const productAttributes = mapProductAttributes(p.metadata);

	const categories = getCategoryHierarchy(p.categories, settings?.productCategoryRootSystemName);
	const primaryCategory = getPrimaryCategory(categories);
	const breadCrumbs: string[] = categories.map((c) => {
		return c.id || '';
	});

	const fullCanonicalLink = productAttributes.url ? new URL(productAttributes.url, origin).href : '';
	const variants = mapVariants(p.skus);

	const currentVariant = variants.find((v) => p.id === v.id);

	const shortDescription = currentVariant?.shortDescription ?? p.shortDescription ?? '';
	const longDescription = currentVariant?.longDescription ?? p.longDescription ?? '';
	const metaTitle = currentVariant?.metaTitle ?? productAttributes.metaTitle ?? '';
	const metaDescription = currentVariant?.metaDescription ?? productAttributes.metaDescription ?? '';
	const metaKeywords = currentVariant?.metaKeywords ?? productAttributes.metaKeywords ?? '';
	const images = (currentVariant?.media?.images ?? p.media)
		?.filter((m) => m.type === MediaModelType.Image)
		.map((m) => mapMediaModel(m, p.name)) || [fallbackImage];
	const videos =
		(currentVariant?.media?.videos ?? p.media)?.filter((m) => m.type === MediaModelType.Video).map(mapVideoModel) ||
		[];
	const files =
		(currentVariant?.media?.files ?? p.media)?.filter((m) => m.type === MediaModelType.File).map(mapFileModel) ||
		[];

	return {
		id: p.id || '',
		itemNo: p.productNumber,
		name: p.name || p.id || '',
		brand: primaryCategory?.name != null ? primaryCategory.name : undefined,
		stock: mapStockStatus(productAttributes.stockStatus),
		variants: variants || [],
		siblings: mapSiblings(p.metadata) || [],
		shortDescription,
		longDescription,
		images,
		specs: mapProductSpecs(p.metadata),
		videos,
		files,
		inserts: currentVariant?.inserts || [],
		breadcrumbs: breadCrumbs,
		pageMetadata: {
			title: metaTitle,
			description: metaDescription,
			keywords: metaKeywords,
			noIndex: false,
			noFollow: false,
			canonicalLink: fullCanonicalLink,
		},
		variantSelectors: productAttributes.variantSelectors || [],
		splashText: productAttributes.splashText || [],
	};
};

export const refineProductItem = (
	item: IncludedItemMetaData,
	insertProductItems: ProductItemResponse[] | null | undefined,
) => {
	const product = insertProductItems?.find((t) => t.id === item.productId) || {};
	const payload: AddOnProduct = {
		image: {},
		name: '',
		itemNo: '',
		quantity: item.quantity,
		id: item.productId,
	};
	return {
		...payload,
		...product,
	};
};
